import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronRight, ChevronDown, Eye, Trash2, ExternalLink, FileText, Sparkles, Info, AlertTriangle, CheckCircle2, AlertCircle, TrendingUp, Minus, Plus, Heart, Hash, Pencil, EyeOff, Search, Type, MapPin, Activity, UtensilsCrossed, Dumbbell, Ruler, ClipboardList, Stethoscope, FlaskConical, Microscope, X, MessageSquare, Check } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState, useEffect, useMemo, useRef } from "react";
import { useDataStale } from "@/contexts/DataStaleContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useModalStore } from "@/contexts/ModalContext";
import { GeminiChatContent } from "@/components/patient/GeminiChatContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  ai_insights?: any[] | null;
  file_path?: string | null;
  extracted_text?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileSessionRowProps {
  session: SmartfileSession;
  isOpen: boolean;
  onToggle: () => void;
  onOpenRegistros?: () => void;
  labResultsCount: number;
  onDelete?: () => void;
  onUpdate?: () => void;
  isEditingDate?: boolean;
  onStartEditDate?: () => void;
  onCancelEditDate?: () => void;
  onSaveDate?: () => void;
}

export function SmartfileSessionRow({
  session,
  isOpen,
  onToggle,
  onOpenRegistros,
  labResultsCount,
  onDelete,
  onUpdate,
  isEditingDate = false,
  onStartEditDate,
  onCancelEditDate,
  onSaveDate
}: SmartfileSessionRowProps) {
  const [hasResultsInDB, setHasResultsInDB] = useState(false);
  const [showTextDialog, setShowTextDialog] = useState(false);
  const recordsSectionRef = useRef<HTMLDivElement>(null);
  const { registerModal, unregisterModal } = useModalStore();
  const [editedDate, setEditedDate] = useState("");
  const [isSavingDate, setIsSavingDate] = useState(false);

  // Estados para Gemini Chat
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [streamingContent, setStreamingContent] = useState('');

  useEffect(() => {
    async function checkResultsInDB() {
      try {
        const { count } = await supabase
          .from("lab_results_onboarding")
          .select("*", { count: "exact", head: true })
          .eq("source_session_id", session.id);

        setHasResultsInDB((count || 0) > 0);
      } catch (error) {
        console.error("Error checking lab results:", error);
        setHasResultsInDB(false);
      }
    }

    checkResultsInDB();
  }, [session.id]);

  // Registrar/desregistrar modais no store
  useEffect(() => {
    const textModalId = `text-dialog-${session.id}`;
    if (showTextDialog) {
      registerModal(textModalId);
    } else {
      unregisterModal(textModalId);
    }
    return () => {
      unregisterModal(textModalId);
    };
  }, [showTextDialog, session.id, registerModal, unregisterModal]);

  const formattedDate = format(new Date(session.created_at), "dd/MM/yyyy", {
    locale: ptBR,
  });

  // Inicializar editedDate quando começar a editar
  useEffect(() => {
    if (isEditingDate) {
      // Converter data para formato YYYY-MM-DD usando timezone local
      const dateObj = new Date(session.created_at);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      setEditedDate(`${year}-${month}-${day}`);
    } else {
      setEditedDate("");
    }
  }, [isEditingDate, session.created_at]);

  const handleStartEditDate = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onStartEditDate?.();
  };

  const handleCancelEditDate = () => {
    setEditedDate("");
    onCancelEditDate?.();
  };

  const handleSaveDate = async () => {
    if (!editedDate) return;

    try {
      setIsSavingDate(true);
      // Converter data do formato YYYY-MM-DD para ISO string usando timezone local
      const [year, month, day] = editedDate.split('-').map(Number);
      // Criar data no timezone local (não UTC)
      const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0);

      const { error } = await supabase
        .from("smartfile_sessions")
        .update({ created_at: dateObj.toISOString() })
        .eq("id", session.id);

      if (error) throw error;

      toast.success("Data atualizada com sucesso!");
      setEditedDate("");
      onSaveDate?.();
    } catch (error) {
      console.error("Erro ao salvar data:", error);
      toast.error("Erro ao salvar data");
    } finally {
      setIsSavingDate(false);
    }
  };

  // Detectar tipo de registro principal
  const hasMealPlanWeekly = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) => {
      if (typeof r !== 'object' || r === null || !('type' in r)) return false;
      if (r.type !== 'meal_plan') return false;
      const mealPlanRecord = r as { structure_type?: string; is_main_record?: boolean | string };
      const isMainRecord = mealPlanRecord.is_main_record === true || mealPlanRecord.is_main_record === 'true';
      return mealPlanRecord.structure_type === 'weekly' && !isMainRecord;
    });

  const hasMealPlanFood = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) => {
      if (typeof r !== 'object' || r === null || !('type' in r)) return false;
      if (r.type !== 'meal_plan') return false;
      const mealPlanRecord = r as { structure_type?: string; is_main_record?: boolean | string };
      const isMainRecord = mealPlanRecord.is_main_record === true || mealPlanRecord.is_main_record === 'true';
      return mealPlanRecord.structure_type === 'food' && !isMainRecord;
    });

  const hasMealPlan = hasMealPlanWeekly || hasMealPlanFood;

  const hasTrainingPlan = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) =>
      typeof r === 'object' && r !== null && 'type' in r && r.type === 'training_plan'
    );

  const hasLabResult = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) =>
      typeof r === 'object' && r !== null && 'type' in r && r.type === 'lab_result'
    );

  const hasPhysiotherapyEvolution = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) => {
      if (typeof r !== 'object' || r === null || !('type' in r)) return false;
      // Verificar se é physiotherapy_evolution (incluindo registro principal e sub-registros)
      return r.type === 'physiotherapy_evolution';
    });

  const hasAnthropometry = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) => {
      if (typeof r !== 'object' || r === null || !('type' in r)) return false;
      if (r.type !== 'anthropometry') return false;
      const anthropometryRecord = r as { is_main_record?: boolean | string };
      const isMainRecord = anthropometryRecord.is_main_record === true || anthropometryRecord.is_main_record === 'true';
      return !isMainRecord;
    });

  const hasAnamnesis = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) => {
      if (typeof r !== 'object' || r === null || !('type' in r)) return false;
      if (r.type !== 'anamnesis') return false;
      const anamnesisRecord = r as { is_main_record?: boolean | string };
      const isMainRecord = anamnesisRecord.is_main_record === true || anamnesisRecord.is_main_record === 'true';
      return !isMainRecord;
    });

  const hasMedicalReport = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) => {
      if (typeof r !== 'object' || r === null || !('type' in r)) return false;
      return r.type === 'medical_report';
    });

  const hasBiopsyReport = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) => {
      if (typeof r !== 'object' || r === null || !('type' in r)) return false;
      return r.type === 'biopsy_report';
    });

  const hasVitalSign = session.structured_records && Array.isArray(session.structured_records) &&
    session.structured_records.some((r: any) => {
      if (typeof r !== 'object' || r === null || !('type' in r)) return false;
      return r.type === 'vital_sign';
    });

  // ✅ CORREÇÃO: Respeitar content_type primeiro, depois verificar structured_records
  // Se content_type for "other", não deve aparecer como "lab_result" mesmo que tenha registros lab_result
  // Biópsia tem prioridade sobre medical_report
  // Sinais vitais têm prioridade sobre lab_result
  const recordType = session.content_type === 'other' 
    ? 'other' 
    : session.content_type === 'biopsy_report' || hasBiopsyReport ? 'biopsy_report'
    : session.content_type === 'vitals' || hasVitalSign ? 'vitals'
    : hasMealPlan ? 'meal_plan' 
    : hasTrainingPlan ? 'training_plan' 
    : hasPhysiotherapyEvolution ? 'physiotherapy_evolution' 
    : hasAnamnesis ? 'anamnesis' 
    : hasMedicalReport ? 'medical_report' 
    : hasAnthropometry ? 'anthropometry' 
    : hasLabResult ? 'lab_result' 
    : (session.content_type || 'other');

  const { markBothStale } = useDataStale();

  async function handleDelete() {
    try {
      // 1. Deletar todos os resultados laboratoriais associados à sessão
      const { error: labResultsError } = await supabase
        .from("lab_results_onboarding")
        .delete()
        .eq("source_session_id", session.id);

      if (labResultsError) {
        console.error("Error deleting lab results:", labResultsError);
        // Continuar mesmo com erro - tentar deletar a sessão
      }

      // 2. Deletar a sessão do smartfile
      const result = await supabase
        .from("smartfile_sessions")
        .delete()
        .match({ id: session.id });

      if (result.error) throw result.error;

      // Marcar visão 360 e análise global como desatualizados
      if (session.patient_id) {
        await markBothStale(session.patient_id);
      }

      toast.success("✅ Sessão excluída com sucesso!");
      onDelete?.();
    } catch (err) {
      console.error("Erro ao excluir:", err);
      toast.error("❌ Erro ao excluir sessão");
    }
  }


  async function handleOpenChat() {
    if (!session.ai_summary) {
      toast.error("Resumo IA não disponível para iniciar chat");
      return;
    }
    setShowChatDialog(true);
    setStreamingContent('');
    
    // Iniciar o chat com o resumo como primeira mensagem do usuário
    const initialMessage = { role: "user" as const, content: `Aqui está o resumo do documento para análise:\n\n${session.ai_summary}` };
    setConversationHistory([initialMessage]);
    setIsLoadingMessage(true);
    
    // Enviar para o LLM responder automaticamente
    try {
      const systemPrompt = `Você é um assistente médico especializado em análise de exames laboratoriais e dados de saúde.
Analise o resumo do documento fornecido e faça uma análise inicial clara e educativa.
Destaque os pontos mais importantes e pergunte se o usuário tem alguma dúvida específica.`;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gemini-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ 
          messages: [
            { role: "system", content: systemPrompt },
            initialMessage
          ] 
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error('Falha ao iniciar chat');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let fullContent = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              fullContent += content;
              setStreamingContent(fullContent);
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      if (fullContent) {
        setConversationHistory(prev => [...prev, { role: "assistant", content: fullContent }]);
        setStreamingContent('');
      }
    } catch (error) {
      console.error('Erro ao iniciar chat:', error);
      toast.error('Erro ao iniciar chat com IA');
    } finally {
      setIsLoadingMessage(false);
    }
  }

  async function handleSendMessage(message: string) {
    if (!session.ai_summary) {
      toast.error("Contexto não disponível");
      return;
    }

    // Adicionar a pergunta do usuário ao histórico
    const userMessage = { role: "user" as const, content: message };
    setConversationHistory(prev => [...prev, userMessage]);
    setIsLoadingMessage(true);
    setStreamingContent('');

    try {
      // Preparar mensagens para o contexto
      const systemPrompt = `Você é um assistente médico especializado em análise de exames laboratoriais e dados de saúde. 
Analise os dados fornecidos e responda às perguntas do usuário de forma clara e educativa.
Sempre que possível, explique os valores de referência e o que significa estar fora da faixa normal.
Se não souber algo, diga claramente que não tem essa informação.

CONTEXTO DO EXAME:
${session.ai_summary}`;

      const messagesForApi = [
        { role: "system" as const, content: systemPrompt },
        ...conversationHistory,
        userMessage
      ];

      // Chamar a edge function com streaming
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gemini-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: messagesForApi }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error("Limite de requisições excedido. Tente novamente em alguns segundos.");
        }
        if (response.status === 402) {
          throw new Error("Créditos insuficientes. Por favor, adicione créditos à sua conta.");
        }
        throw new Error("Erro ao processar requisição");
      }

      if (!response.body) {
        throw new Error("Resposta sem corpo");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let fullContent = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              fullContent += content;
              setStreamingContent(fullContent);
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Adicionar resposta completa ao histórico
      setConversationHistory(prev => [...prev, { role: "assistant" as const, content: fullContent }]);
      setStreamingContent('');
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao enviar mensagem");
      // Remover a pergunta do histórico em caso de erro
      setConversationHistory(prev => prev.slice(0, -1));
    } finally {
      setIsLoadingMessage(false);
    }
  }

  // Verificar se há resultados na tabela lab_results_onboarding OU nos structured_records
  const hasLabResults = hasResultsInDB || (hasLabResult && labResultsCount > 0);

  // Calcular registros agrupados por mês/ano
  const groupedRecordsByMonth = useMemo(() => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return {};
    }

    const records = session.structured_records.filter((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      const recordType = record.type;
      if (recordType === 'physiotherapy_evolution') {
        const physioRecord = record as { is_main_record?: boolean | string; date?: string };
        const isMainRecord = physioRecord.is_main_record === true || physioRecord.is_main_record === 'true';
        return !isMainRecord;
      }
      return false;
    });

    const grouped: Record<string, { displayKey: string; records: any[] }> = {};

    records.forEach((record: any) => {
      const r = record as { date?: string };
      const date = r.date || session.created_at;
      try {
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
          const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

          if (!grouped[monthYearKey]) {
            grouped[monthYearKey] = {
              displayKey,
              records: []
            };
          }
          grouped[monthYearKey].records.push(record);
        }
      } catch (error) {
        console.error('Erro ao processar data:', error);
      }
    });

    return grouped;
  }, [session.structured_records, session.created_at]);

  // Obter primeiro mês/ano para exibir no rodapé
  const firstMonthYear = useMemo(() => {
    const keys = Object.keys(groupedRecordsByMonth).sort();
    return keys.length > 0 ? groupedRecordsByMonth[keys[0]] : null;
  }, [groupedRecordsByMonth]);

  // Contar total de registros
  const totalRecordsCount = useMemo(() => {
    return Object.values(groupedRecordsByMonth).reduce((sum, group) => sum + group.records.length, 0);
  }, [groupedRecordsByMonth]);

  // Obter nome do documento principal
  // Não exibir para physiotherapy_evolution (incluindo antropometria, SPPB e dinamometria)
  const documentName = useMemo(() => {
    if (!session.structured_records || !Array.isArray(session.structured_records)) {
      return null;
    }

    // Não retornar nome para physiotherapy_evolution
    if (recordType === 'physiotherapy_evolution') {
      return null;
    }

    const mainRecord = session.structured_records.find((record: any) => {
      if (typeof record !== 'object' || record === null || !('type' in record)) {
        return false;
      }
      if (record.type !== 'physiotherapy_evolution') return false;
      const physioRecord = record as { is_main_record?: boolean | string; name?: string | null };
      const isMainRecord = physioRecord.is_main_record === true || physioRecord.is_main_record === 'true';
      return isMainRecord;
    }) as { name?: string | null } | undefined;

    return mainRecord?.name || null;
  }, [session.structured_records, recordType]);

  // Obter título do tipo de registro
  const getRecordTypeTitle = () => {
    switch (recordType) {
      case 'physiotherapy_evolution':
        return 'Evolução Fisioterápica';
      case 'meal_plan':
        return 'Plano Alimentar';
      case 'training_plan':
        return 'Plano de Treino';
      case 'anthropometry':
        return 'Antropometria';
      case 'anamnesis':
        return 'Anamnese';
      case 'medical_report':
        return 'Laudo Médico';
      case 'biopsy_report':
        return 'Biópsia';
      case 'vitals':
        return 'Sinais Vitais';
      case 'lab_result':
        return 'Exame Laboratorial';
      default:
        return 'Registro';
    }
  };

  // Obter ícone do tipo de registro
  const getRecordTypeIcon = () => {
    switch (recordType) {
      case 'physiotherapy_evolution':
        return Activity;
      case 'meal_plan':
        return UtensilsCrossed;
      case 'training_plan':
        return Dumbbell;
      case 'anthropometry':
        return Ruler;
      case 'anamnesis':
        return ClipboardList;
      case 'medical_report':
        return Stethoscope;
      case 'biopsy_report':
        return Microscope;
      case 'vitals':
        return Heart;
      case 'lab_result':
        return FlaskConical;
      default:
        return FileText;
    }
  };

  // Obter emoji e cor do tipo de registro
  const getRecordTypeConfig = () => {
    switch (recordType) {
      case 'physiotherapy_evolution':
        return { emoji: '🏃', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'meal_plan':
        return { emoji: '🍽️', color: 'bg-lime-50 text-lime-700 border-lime-200' };
      case 'training_plan':
        return { emoji: '💪', color: 'bg-orange-50 text-orange-700 border-orange-200' };
      case 'anthropometry':
        return { emoji: '📏', color: 'bg-orange-50 text-orange-700 border-orange-200' };
      case 'anamnesis':
        return { emoji: '📋', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'medical_report':
        return { emoji: '📄', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'biopsy_report':
        return { emoji: '🔬', color: 'bg-rose-50 text-rose-700 border-rose-200' };
      case 'vitals':
        return { emoji: '❤️', color: 'bg-red-50 text-red-700 border-red-200' };
      case 'lab_result':
        return { emoji: '🔬', color: 'bg-teal-50 text-teal-700 border-teal-200' };
      default:
        return { emoji: '📄', color: 'bg-gray-50 text-gray-700 border-gray-200' };
    }
  };

  // Função para obter configuração do insight baseado no tipo
  const getInsightConfig = (type: string) => {
    const configs: Record<string, {
      icon: typeof Info | typeof AlertTriangle | typeof CheckCircle2 | typeof AlertCircle | typeof TrendingUp;
      bgColor: string;
      textColor: string;
      borderColor: string;
    }> = {
      info: {
        icon: Info,
        bgColor: "bg-blue-50 dark:bg-blue-950/20",
        textColor: "text-blue-700 dark:text-blue-300",
        borderColor: "border-blue-200 dark:border-blue-800"
      },
      warning: {
        icon: AlertTriangle,
        bgColor: "bg-amber-50 dark:bg-amber-950/20",
        textColor: "text-amber-700 dark:text-amber-300",
        borderColor: "border-amber-200 dark:border-amber-800"
      },
      success: {
        icon: CheckCircle2,
        bgColor: "bg-green-50 dark:bg-green-950/20",
        textColor: "text-green-700 dark:text-green-300",
        borderColor: "border-green-200 dark:border-green-800"
      },
      error: {
        icon: AlertCircle,
        bgColor: "bg-red-50 dark:bg-red-950/20",
        textColor: "text-red-700 dark:text-red-300",
        borderColor: "border-red-200 dark:border-red-800"
      },
      default: {
        icon: TrendingUp,
        bgColor: "bg-purple-50 dark:bg-purple-950/20",
        textColor: "text-purple-700 dark:text-purple-300",
        borderColor: "border-purple-200 dark:border-purple-800"
      }
    };

    return configs[type.toLowerCase()] || configs.default;
  };

  async function handleOpenOriginalFile() {
    if (!session.file_path) {
      toast.error("Arquivo original não disponível");
      return;
    }

    try {
      const bucket = 'patient-docs';
      const filePath = session.file_path;

      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error("Erro ao abrir arquivo: " + error.message);
        return;
      }

      if (data?.signedUrl) {
        window.open(data.signedUrl, "_blank");
      } else {
        toast.error("URL do arquivo não disponível");
      }
    } catch (error) {
      console.error("Error opening file:", error);
      toast.error("Erro ao abrir arquivo");
    }
  }

  const [showInsights, setShowInsights] = useState(false);

  return (
    <div className="w-full bg-background border-b">
      <div className="w-full">
        {/* Cabeçalho - mesma estrutura sempre */}
        {/* Cabeçalho - Layout em Grid */}
        <div
          className="grid grid-cols-12 gap-2 px-4 py-3 border-b items-center cursor-pointer min-w-[800px] overflow-visible"
          onClick={onToggle}
        >
          <div className="col-span-1">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="flex items-center justify-center w-6 h-6 rounded-full p-0"
            >
              {isOpen ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
            </Button>
          </div>

          <div 
            className="col-span-4 md:col-span-2 text-sm font-medium flex items-center gap-1 min-w-0"
            onClick={(e) => e.stopPropagation()}
          >
            {isEditingDate ? (
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <Input
                  type="date"
                  value={editedDate}
                  onChange={(e) => setEditedDate(e.target.value)}
                  className="h-7 text-xs w-[110px] flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveDate();
                    } else if (e.key === 'Escape') {
                      handleCancelEditDate();
                    }
                  }}
                  autoFocus
                />
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 px-2 text-xs whitespace-nowrap flex-shrink-0"
                  onClick={handleSaveDate}
                  disabled={isSavingDate}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Salvar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs whitespace-nowrap flex-shrink-0"
                  onClick={handleCancelEditDate}
                  disabled={isSavingDate}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1 min-w-0 w-full">
                <span className="truncate">{formattedDate}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 w-5 p-0 flex-shrink-0 hover:bg-muted ml-0.5"
                  onClick={handleStartEditDate}
                  title="Editar data"
                >
                  <Pencil className="h-3 w-3 text-muted-foreground" />
                </Button>
              </div>
            )}
          </div>

          <div className="col-span-7 md:col-span-2 flex items-center gap-2 overflow-hidden">
            {(() => {
              const typeConfig = getRecordTypeConfig();
              return <span className="text-base flex-shrink-0">{typeConfig.emoji}</span>;
            })()}
            <h3 className="text-sm font-semibold truncate" title={getRecordTypeTitle()}>
              {getRecordTypeTitle()}
            </h3>
          </div>

          <div className="hidden md:block md:col-span-3 text-sm text-muted-foreground truncate" title={session.file_name}>
            {session.file_name}
          </div>

          {/* Botões de Ação - Apenas Desktop */}
          <div className="hidden md:flex md:col-span-4 items-center justify-start gap-1.5 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 px-2.5 py-1.5 h-auto text-xs whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                if (!isOpen) {
                  onOpenRegistros?.();
                }
              }}
            >
              <Search className="h-3.5 w-3.5" />
              <span className="text-xs">Registros</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 px-2.5 py-1.5 h-auto text-xs whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                if (session.extracted_text) {
                  setShowTextDialog(true);
                }
              }}
            >
              <Type className="h-3.5 w-3.5" />
              <span className="text-xs">Texto</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 px-2.5 py-1.5 h-auto text-xs whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                if (session.file_path) {
                  handleOpenOriginalFile();
                }
              }}
            >
              <FileText className="h-3.5 w-3.5" />
              <span className="text-xs">Documento</span>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 px-2.5 py-1.5 h-auto text-xs whitespace-nowrap text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="text-xs">Excluir</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir esta sessão?
                    Todos os registros estruturados associados serão removidos.
                    Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Sim, Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Conteúdo expandido - só mostra quando aberto */}
        {isOpen && (
          <div onClick={(e) => e.stopPropagation()}>

            {/* Botões de Ação - Mobile/Tablet */}
            <div className="md:hidden px-3 py-2 border-b">
              <span className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">AÇÕES</span>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center justify-center gap-1.5 px-3 py-1.5 h-auto w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenRegistros?.();
                  }}
                >
                  <Search className="h-4 w-4 text-primary" />
                  <span className="text-xs">Registros</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center justify-center gap-1.5 px-3 py-1.5 h-auto w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (session.extracted_text) {
                      setShowTextDialog(true);
                    }
                  }}
                >
                  <Type className="h-4 w-4 text-primary" />
                  <span className="text-xs">Texto</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center justify-center gap-1.5 px-3 py-1.5 h-auto w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (session.file_path) {
                      handleOpenOriginalFile();
                    }
                  }}
                >
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="text-xs">Documento</span>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center justify-center gap-1.5 px-3 py-1.5 h-auto w-full text-destructive border-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="text-xs">Excluir</span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir esta sessão?
                        Todos os registros estruturados associados serão removidos.
                        Esta ação não pode ser desfeita.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Sim, Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            {/* Resumo IA */}
            {session.ai_summary && (
              <div className="px-4 py-3 border-b">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-primary" />
                    <h4 className="text-sm font-semibold">Resumo IA</h4>
                  </div>

                  {/* Botão para abrir Consulta Chat - apenas para LAB_RESULTS, ANAMNESE e LAUDO_MEDICO */}
                  {(recordType === 'lab_result' || recordType === 'anamnesis' || recordType === 'medical_report') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleOpenChat}
                      className="h-7 text-xs"
                    >
                      <MessageSquare className="h-3 w-3 mr-1.5" />
                      Consulta Chat
                    </Button>
                  )}
                </div>
                <p className="text-xs text-foreground leading-relaxed">{session.ai_summary}</p>
              </div>
            )}

            {/* Link para Ver Insights */}
            {session.ai_insights && Array.isArray(session.ai_insights) && session.ai_insights.length > 0 && (
              <div className="px-4 py-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowInsights(!showInsights);
                  }}
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  <MapPin className="h-3 w-3" />
                  Ver Insights
                </button>
                {showInsights && (
                  <div className="space-y-2 mt-3">
                    {session.ai_insights.map((insight: any, index: number) => {
                      const insightObj = insight as {
                        type?: string;
                        title?: string;
                        message?: string;
                        description?: string;
                        content?: string;
                      };

                      const type = insightObj.type || 'info';
                      const config = getInsightConfig(type);
                      const Icon = config.icon;
                      const title = insightObj.title || `Insight ${index + 1}`;
                      const message = insightObj.message || insightObj.description || insightObj.content || '';

                      return (
                        <div
                          key={index}
                          className={`${config.bgColor} ${config.borderColor} border rounded-lg p-3`}
                        >
                          <div className="flex items-start gap-2">
                            <Icon className={`h-4 w-4 ${config.textColor} mt-0.5 flex-shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <h5 className={`text-sm font-semibold ${config.textColor} mb-1`}>{title}</h5>
                              {message && (
                                <p className={`text-xs ${config.textColor} opacity-90 leading-relaxed`}>{message}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Nome do Documento - Não exibir para physiotherapy_evolution */}
            {documentName && recordType !== 'physiotherapy_evolution' && (
              <div className="px-4 py-3 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs text-muted-foreground">Nome do Documento</Label>
                    <p className="text-sm font-medium mt-1">{documentName}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Aqui você pode adicionar lógica para editar o nome do documento
                    }}
                    className="h-7 px-2 text-xs"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Editar
                  </Button>
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {/* Dialog para exibir texto extraído - renderizado em portal para evitar conflitos */}
      {showTextDialog && (
        <Dialog
          open={showTextDialog}
          onOpenChange={(open) => {
            if (!open) {
              setShowTextDialog(false);
            }
          }}
          modal={true}
        >
          <DialogContent
            className="max-w-3xl max-h-[80vh] overflow-y-auto"
          >
            <DialogHeader>
              <DialogTitle>Texto Extraído</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <pre className="text-sm whitespace-pre-wrap font-mono bg-muted p-4 rounded-lg overflow-x-auto">
                {session.extracted_text}
              </pre>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para Consulta Chat com Gemini */}
      {showChatDialog && (
        <Dialog
          open={showChatDialog}
          onOpenChange={(open) => {
            if (!open) {
              setShowChatDialog(false);
              setConversationHistory([]);
              setStreamingContent('');
            }
          }}
          modal={true}
        >
          <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Consulta Chat
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden mt-4">
              <GeminiChatContent
                initialContext={session.ai_summary || ''}
                conversationHistory={conversationHistory}
                onSendMessage={handleSendMessage}
                isLoadingMessage={isLoadingMessage}
                streamingContent={streamingContent}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

